/**
 * Post Affiliate Pro click + visitor capture for Totara Health Stripe checkout.
 *
 * Last-click is PAP's native cookie (PAPVisitorId), not a custom attribution layer.
 * Commission dollars are never taken from the browser — the Stripe webhook maps them.
 */
import {
  getMedicationFamily,
  getPlanMonths,
} from "./affiliateCommission";
import { PAP_PRODUCT_KEYS } from "./papCommissionMap";

export const PAP_ACCOUNT_ID =
  process.env.REACT_APP_PAP_ACCOUNT_ID || "default1";

export const PAP_TRACKING_SCRIPT_URL =
  process.env.REACT_APP_PAP_TRACKING_SCRIPT_URL ||
  "https://totarahealth.postaffiliatepro.com/scripts/jlhznh6xl";

export const PAP_COOKIE_NAME = "PAPVisitorId";
export const PAP_STORAGE_KEY = "pap_visitor_id";
export const PAP_AFFILIATE_STORAGE_KEY = "pap_a_aid";
const PAP_SALE_TRACKED_PREFIX = "pap_sale_tracked_";

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const parts = document.cookie.split("; ");
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq) === name) {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return part.slice(eq + 1);
      }
    }
  }
  return "";
};

const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (key, value) => {
  if (!value) return;
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
};

/** Full PAP cookie (account prefix + 32-char visitor id). */
export const getPapCookie = () => {
  const fromCookie = readCookie(PAP_COOKIE_NAME);
  if (fromCookie) {
    writeStorage(PAP_STORAGE_KEY, fromCookie);
    return fromCookie;
  }

  try {
    if (window.PostAffTracker?._cmanager?.getVisitorIdOrSaleCookieValue) {
      const visitor =
        window.PostAffTracker._cmanager.getVisitorIdOrSaleCookieValue();
      const account =
        typeof window.PostAffTracker._getAccountId === "function"
          ? window.PostAffTracker._getAccountId()
          : PAP_ACCOUNT_ID;
      if (visitor) {
        const combined =
          String(visitor).length >= 40
            ? String(visitor)
            : `${account || ""}${visitor}`;
        writeStorage(PAP_STORAGE_KEY, combined);
        return combined;
      }
    }
  } catch {
    /* tracker not ready */
  }

  return readStorage(PAP_STORAGE_KEY);
};

/**
 * 32-char visitor id for PAP sale.php (strip 8-char account prefix when present).
 */
export const getPapVisitorId = (cookieValue = getPapCookie()) => {
  const raw = String(cookieValue || "").trim();
  if (!raw) return "";
  if (raw.length >= 40) return raw.slice(-32);
  if (raw.length === 32) return raw;
  return raw;
};

/** Last a_aid seen on this checkout (backup only if cookie is missing). */
export const getPapAffiliateId = () => readStorage(PAP_AFFILIATE_STORAGE_KEY);

export const capturePapUrlParams = () => {
  if (typeof window === "undefined") return;
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(
    String(window.location.hash || "").replace(/^#/, "")
  );
  const aid =
    search.get("a_aid") ||
    search.get("ref") ||
    hash.get("a_aid") ||
    hash.get("ref");
  if (aid) writeStorage(PAP_AFFILIATE_STORAGE_KEY, aid);
};

export const persistPapVisitor = () => {
  capturePapUrlParams();
  return getPapCookie();
};

/**
 * Poll until PAP sets its cookie after track(), then invoke onChange.
 * Returns a cleanup function.
 */
export const startPapVisitorCapture = (onChange) => {
  if (typeof window === "undefined") return () => {};

  const emit = () => {
    const cookie = persistPapVisitor();
    if (cookie && typeof onChange === "function") onChange(cookie);
    return Boolean(cookie);
  };

  emit();
  const interval = window.setInterval(() => {
    if (emit()) window.clearInterval(interval);
  }, 300);
  const timeout = window.setTimeout(() => window.clearInterval(interval), 8000);

  return () => {
    window.clearInterval(interval);
    window.clearTimeout(timeout);
  };
};

export const getPapProductKey = (product = {}) => {
  const family = getMedicationFamily(
    product.product_name || product.product_title || ""
  );
  const months = getPlanMonths(product);
  if (family === "other") return "";
  const key = `${family}-${months}-month`;
  return Object.values(PAP_PRODUCT_KEYS).includes(key) ? key : "";
};

export const getPapProductKeyFromList = (products = []) => {
  const keys = (Array.isArray(products) ? products : [])
    .map(getPapProductKey)
    .filter(Boolean);
  return [...new Set(keys)].join(",");
};

export const getPapTrackingFields = ({ products = [] } = {}) => {
  const papCookie = persistPapVisitor();
  const papVisitorId = getPapVisitorId(papCookie);
  const papAffiliateId = getPapAffiliateId();
  const fields = {
    papCookie: papCookie || "",
    papVisitorId: papVisitorId || "",
    papProductKey: getPapProductKeyFromList(products),
  };
  if (papAffiliateId) fields.papAffiliateId = papAffiliateId;
  return fields;
};

const loadPapTracker = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.PostAffTracker) {
      resolve(window.PostAffTracker);
      return;
    }
    let script = document.getElementById("pap_x2s6df8d");
    const onReady = () => {
      if (window.PostAffTracker) resolve(window.PostAffTracker);
      else reject(new Error("PostAffTracker missing after script load"));
    };
    if (script) {
      if (window.PostAffTracker) onReady();
      else script.addEventListener("load", onReady, { once: true });
      return;
    }
    script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "pap_x2s6df8d";
    script.src = PAP_TRACKING_SCRIPT_URL;
    script.onload = onReady;
    script.onerror = () => reject(new Error("Failed to load PAP tracker"));
    document.head.appendChild(script);
  });

const saleTrackedKey = (orderId) => `${PAP_SALE_TRACKED_PREFIX}${orderId}`;

export const wasPapSaleTracked = (orderId) => {
  if (!orderId) return false;
  try {
    return sessionStorage.getItem(saleTrackedKey(orderId)) === "1";
  } catch {
    return false;
  }
};

export const markPapSaleTracked = (orderId) => {
  if (!orderId) return;
  try {
    sessionStorage.setItem(saleTrackedKey(orderId), "1");
  } catch {
    /* ignore */
  }
};

/**
 * PAP sales script (merchant panel snippet):
 *
 * PostAffTracker.setAccountId('default1');
 * var sale = PostAffTracker.createSale();
 * sale.setTotalCost('120.50');
 * sale.setOrderID('ORD_12345XYZ');
 * sale.setProductID('test product');
 * PostAffTracker.register();
 *
 * Placeholders are replaced with the real Stripe order. Same tracker script
 * as click tracking (jlhznh6xl). OrderID is the PaymentIntent id when present
 * so the webhook can decline duplicates.
 */
export const trackPapSale = async ({
  orderId,
  totalCost,
  productId,
  paymentIntentId,
}) => {
  const stableOrderId = String(paymentIntentId || orderId || "").trim();
  if (!stableOrderId) return;

  if (wasPapSaleTracked(stableOrderId)) return;

  const total =
    totalCost == null || totalCost === ""
      ? ""
      : Number(totalCost).toFixed(2);

  try {
    const PostAffTracker = await loadPapTracker();
    PostAffTracker.setAccountId(PAP_ACCOUNT_ID);

    var sale = PostAffTracker.createSale();
    sale.setTotalCost(total || "0.00");
    sale.setOrderID(stableOrderId);
    sale.setProductID(String(productId || ""));

    PostAffTracker.register();
    markPapSaleTracked(stableOrderId);
  } catch (err) {
    console.error("[pap] sale tracking error:", err);
  }
};
